import * as THREE from 'three';

const MAX = 700;
const STRIDE = 0.72;

/**
 * Footprints in the snow: one instanced mesh, a ring buffer of prints. Hers and
 * his are laid as they walk; a level can also leave a trail that was already
 * there before she arrived.
 */
export class Footprints {
  readonly mesh: THREE.InstancedMesh;
  private next = 0;
  private count = 0;
  private readonly dummy = new THREE.Object3D();
  private readonly walkers = new Map<string, { x: number; z: number; left: boolean; dist: number }>();

  constructor() {
    const geometry = new THREE.CircleGeometry(0.5, 10);
    geometry.rotateX(-Math.PI / 2);
    const material = new THREE.MeshStandardMaterial({
      color: '#59606b',
      roughness: 1,
      transparent: true,
      opacity: 0.55,
      depthWrite: false,
      polygonOffset: true,
      polygonOffsetFactor: -3,
    });
    this.mesh = new THREE.InstancedMesh(geometry, material, MAX);
    this.mesh.count = 0;
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = 1;
    this.mesh.visible = false;
  }

  set enabled(on: boolean) {
    this.mesh.visible = on;
  }

  get enabled(): boolean {
    return this.mesh.visible;
  }

  clear(): void {
    this.next = 0;
    this.count = 0;
    this.mesh.count = 0;
    this.walkers.clear();
  }

  /** Lay one print: `side` is -1 for left, 1 for right. */
  add(x: number, z: number, heading: number, side: number, scale = 1): void {
    const fx = -Math.sin(heading);
    const fz = -Math.cos(heading);
    // A shoe print: about 10 cm wide and 26 cm long, feet a hand's width apart.
    const off = 0.09 * side * scale;
    this.dummy.position.set(x - fz * off, 0.035, z + fx * off);
    this.dummy.rotation.set(0, heading, 0);
    this.dummy.scale.set(0.1 * scale, 1, 0.26 * scale);
    this.dummy.updateMatrix();
    this.mesh.setMatrixAt(this.next, this.dummy.matrix);
    this.next = (this.next + 1) % MAX;
    this.count = Math.min(MAX, this.count + 1);
    this.mesh.count = this.count;
    this.mesh.instanceMatrix.needsUpdate = true;
  }

  /** Feed a walker's position each frame; prints drop every stride. */
  track(id: string, x: number, z: number, scale = 1): void {
    if (!this.mesh.visible) return;
    const w = this.walkers.get(id);
    if (!w) {
      this.walkers.set(id, { x, z, left: false, dist: 0 });
      return;
    }
    const d = Math.hypot(x - w.x, z - w.z);
    if (d > 4) {
      // Teleported (restart or a placed stranger): start fresh.
      w.x = x;
      w.z = z;
      w.dist = 0;
      return;
    }
    w.dist += d;
    if (w.dist >= STRIDE * scale && d > 0.0001) {
      const heading = Math.atan2(-(x - w.x), -(z - w.z));
      w.left = !w.left;
      this.add(x, z, heading, w.left ? -1 : 1, scale);
      w.dist = 0;
    }
    w.x = x;
    w.z = z;
  }

  /** Lay prints along a polyline of world points (an older trail). */
  trail(points: THREE.Vector3[], scale = 1.1): void {
    let left = false;
    for (let i = 1; i < points.length; i += 1) {
      const a = points[i - 1];
      const b = points[i];
      const len = Math.hypot(b.x - a.x, b.z - a.z);
      const heading = Math.atan2(-(b.x - a.x), -(b.z - a.z));
      for (let s = 0; s < len; s += STRIDE * scale) {
        left = !left;
        const t = s / len;
        this.add(a.x + (b.x - a.x) * t, a.z + (b.z - a.z) * t, heading, left ? -1 : 1, scale);
      }
    }
  }

  dispose(): void {
    this.mesh.geometry.dispose();
    (this.mesh.material as THREE.Material).dispose();
  }
}
